import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/api/client';
import AddressFields from './AddressFields';
import { FieldError } from './form-utils';
import { errorClass, useFieldErrors } from './use-field-errors';

export default function DeliveryAddressForm({ address, onClose }) {
  const queryClient = useQueryClient();
  const { fieldErrors, handleError, clearErrors } = useFieldErrors();

  const [formData, setFormData] = useState({
    name: address?.name ?? '',
    address1: address?.address1 ?? '',
    address2: address?.address2 ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    zip: address?.zip ?? '',
    country: address?.country ?? 'US',
  });

  const saveMutation = useMutation({
    mutationFn: (data) =>
      address
        ? api.entities.DeliveryAddress.update(address.id, data)
        : api.entities.DeliveryAddress.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveryAddresses'] });
      toast.success(address ? 'Address updated' : 'Address saved');
      onClose();
    },
    onError: (error) => handleError(error, 'Failed to save address'),
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    clearErrors();
    saveMutation.mutate(formData);
  };

  return (
    <Card className="border-green-200 shadow-md">
      <CardHeader className="bg-green-50 border-b flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg flex items-center gap-2">
          <MapPin className="w-5 h-5 text-green-600" />
          {address ? 'Edit delivery address' : 'Add delivery address'}
        </CardTitle>
        <Button type="button" variant="ghost" size="icon" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="da-name">Recipient name</Label>
            <Input
              id="da-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Jane Doe"
              className={errorClass(fieldErrors.name)}
              required
            />
            <FieldError message={fieldErrors.name} />
          </div>

          <AddressFields
            formData={formData}
            setFormData={setFormData}
            fieldErrors={fieldErrors}
            idPrefix="da"
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saveMutation.isPending}
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saveMutation.isPending ? 'Saving…' : 'Save address'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
